import { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { Home, Search, Clapperboard, Briefcase, MessageSquare, Crown, ShieldCheck, Menu, LogOut, Settings, User, Info, LifeBuoy } from "lucide-react";
import { Logo } from "@/components/Brand";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/constants";
import { WhatsAppSupport, SUPPORT_LINK } from "@/components/WhatsAppSupport";
import { AppPromo, OpenInAppBar } from "@/components/AppPromo";

const NAV = [
  { to: "/feed", label: "Home Feed", icon: Home, key: "feed" },
  { to: "/discover", label: "Discover", icon: Search, key: "discover" },
  { to: "/reels", label: "Reels", icon: Clapperboard, key: "reels" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, key: "jobs" },
  { to: "/messages", label: "Messages", icon: MessageSquare, key: "messages" },
  { to: "/pricing", label: "Membership", icon: Crown, key: "pricing" },
];

function NavItems({ onClick, isAdmin }) {
  const items = isAdmin ? [...NAV, { to: "/admin", label: "Admin Panel", icon: ShieldCheck, key: "admin" }] : NAV;
  return (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, label, icon: Icon, key }) => (
        <NavLink key={key} to={to} onClick={onClick} data-testid={`nav-${key}-link`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-tm-green text-white" : "text-slate-700 hover:bg-tm-muted"}`}>
          <Icon className="h-[18px] w-[18px]" /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) {
    return (
      <div className="flex gap-2">
        <Button variant="ghost" asChild data-testid="header-login-button"><Link to="/login">Log in</Link></Button>
        <Button asChild className="bg-tm-green hover:bg-tm-green-light rounded-full" data-testid="header-join-button"><Link to="/register">Join us</Link></Button>
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded-full outline-none ring-tm-gold focus-visible:ring-2" data-testid="user-menu-trigger">
        <UserAvatar user={user} size="sm" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <div className="px-2 py-1.5 text-sm font-semibold truncate">{user.full_name}</div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate(`/profile/${user.id}`)} data-testid="menu-profile"><User className="mr-2 h-4 w-4" />My profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings"><Settings className="mr-2 h-4 w-4" />Edit profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.open(SUPPORT_LINK, "_blank", "noopener")} data-testid="menu-contact-support"><LifeBuoy className="mr-2 h-4 w-4" />Contact support</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/about")} data-testid="menu-about"><Info className="mr-2 h-4 w-4" />About us</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Header() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const submit = (e) => { e.preventDefault(); navigate(`/discover?q=${encodeURIComponent(q)}`); };
  return (
    <header className="sticky top-0 z-30 border-b bg-white/90 backdrop-blur-md">
      <OpenInAppBar />
      <div className="flex h-16 items-center gap-3 px-4 lg:px-8">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden"><Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu /></Button></SheetTrigger>
          <SheetContent side="left" className="w-72 bg-tm-sand">
            <div className="mb-8"><Logo /></div>
            <NavItems onClick={() => setOpen(false)} isAdmin={isAdmin(user)} />
          </SheetContent>
        </Sheet>
        <div className="lg:hidden"><Logo to={user ? "/feed" : "/"} /></div>
        <form onSubmit={submit} className="hidden md:block flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search CAs, CSs, tax experts, services..." className="pl-9 rounded-full bg-tm-muted border-transparent" data-testid="header-search-input" />
          </div>
        </form>
        <div className="ml-auto"><UserMenu /></div>
      </div>
    </header>
  );
}

function MobileBar() {
  return (
    <nav className="fixed bottom-0 inset-x-0 z-30 grid grid-cols-5 border-t bg-white/95 backdrop-blur-md lg:hidden">
      {NAV.slice(0, 5).map(({ to, label, icon: Icon, key }) => (
        <NavLink key={key} to={to} data-testid={`mobile-nav-${key}`} className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[11px] ${isActive ? "text-tm-green font-semibold" : "text-slate-500"}`}>
          <Icon className="h-5 w-5" /> {label.split(" ")[0]}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-tm-sand">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r bg-white px-5 py-6 lg:flex">
        <Logo to={user ? "/feed" : "/"} />
        <div className="mt-10"><NavItems isAdmin={isAdmin(user)} /></div>
        <Link to="/pricing" className="mt-auto rounded-2xl bg-tm-green p-5 text-white tm-noise tm-lift" data-testid="sidebar-diwali-cta">
          <p className="text-[11px] uppercase tracking-[0.2em] text-tm-saffron font-semibold">Diwali Offer</p>
          <p className="mt-2 font-display text-lg font-bold leading-snug">Free membership until 15 Nov</p>
          <p className="mt-1 text-xs text-white/70">Lock in today's price before it doubles</p>
        </Link>
      </aside>
      <div className="lg:pl-64">
        <Header />
        <main className="px-4 py-6 pb-24 lg:px-8 lg:pb-10"><Outlet /></main>
      </div>
      <MobileBar />
      <WhatsAppSupport />
      <AppPromo />
    </div>
  );
}
